import { useEffect, useRef } from 'react';

import {
  Button,
  OtpInput,
  type OtpInputHandle,
  Spinner,
  theme,
  Typography,
} from '@/shared/design-system';

import { OtpBlock, PendingStatus, VerificationContent } from './wallet-mfa.styled';
import { WalletMfaVerificationPhase, WalletMfaVerificationPurpose } from './wallet-mfa-types';
import type { WalletMfaVerificationProps } from './wallet-mfa-verification.types';

const Pending = ({ label }: { label: string }) => (
  <PendingStatus role="status" aria-live="polite" aria-busy="true">
    <Spinner />
    <Typography variant="body3" color="inherit">
      {label}
    </Typography>
  </PendingStatus>
);

const VerificationError = ({
  message,
  onRestart,
  showRetry,
}: {
  message: string | null;
  onRestart: () => void;
  showRetry: boolean;
}) => (
  <VerificationContent>
    <Typography role="alert" variant="body3" color={theme.colors.text.error}>
      {message ?? 'Wallet confirmation could not be completed. Please try again.'}
    </Typography>
    {showRetry && (
      <Button variant="outline" onClick={onRestart}>
        Retry
      </Button>
    )}
  </VerificationContent>
);

export const getWalletMfaVerificationTitle = (purpose: WalletMfaVerificationPurpose) =>
  purpose === WalletMfaVerificationPurpose.SecurityChange
    ? 'Confirm Wallet Security Change'
    : 'Confirm Wallet Transaction';

export const WalletMfaVerificationContent = ({
  phase,
  code,
  error,
  onCodeChange,
  onRestart,
  showErrorRetry = true,
}: Omit<WalletMfaVerificationProps, 'open' | 'onCancel'> & {
  showErrorRetry?: boolean;
}) => {
  const otpRef = useRef<OtpInputHandle>(null);

  useEffect(() => {
    if (phase === WalletMfaVerificationPhase.Totp && error) otpRef.current?.focusFirst();
  }, [error, phase]);

  if (phase === WalletMfaVerificationPhase.Totp) {
    return (
      <VerificationContent>
        <Typography variant="body2" color={theme.colors.text.secondary}>
          Enter the six-digit code from your authenticator app.
        </Typography>
        <OtpBlock>
          <OtpInput
            ref={otpRef}
            length={6}
            groupSize={3}
            value={code}
            onChange={onCodeChange}
            error={Boolean(error)}
          />
          {error && (
            <Typography role="alert" variant="body3" color={theme.colors.text.error}>
              {error}
            </Typography>
          )}
        </OtpBlock>
      </VerificationContent>
    );
  }

  if (
    phase === WalletMfaVerificationPhase.TotpStarting ||
    phase === WalletMfaVerificationPhase.TotpSubmitting
  ) {
    return <Pending label="Checking your authenticator code…" />;
  }

  return <VerificationError message={error} onRestart={onRestart} showRetry={showErrorRetry} />;
};
